import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Fingerprint, FileSearch, Sparkles, Send, RefreshCw, ShieldCheck } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { PageHero, Section } from "@/components/PageShell";
import { IMG } from "@/data/images";

const HELP = [
  [MapPin, "Verify origin", "Confirm a diamond's Botswana origin through connected, documented records."],
  [Fingerprint, "Authenticate the physical diamond", "Match the stone to its captured physical identity via approved systems."],
  [FileSearch, "Access supporting records", "Independent laboratory reports, imaging and traceability data in one place."],
  [Sparkles, "Present the Botswana journey", "Share a beautiful, credible origin story with your consumers."],
  [Send, "Request eligible inventory", "Enquire about verified diamonds released to approved buyers."],
  [RefreshCw, "Verify & transfer after purchase", "Confirm a diamond after sale and update ownership records securely."],
];

export default function ForBuyers() {
  return (
    <div>
      <PageHero
        eyebrow="For International Buyers & Retailers"
        title="Origin you can present. Identity you can prove."
        subtitle="Botswana Brilliance helps buyers and retailers verify origin, authenticate the physical diamond, access supporting records and present a credible Botswana journey to consumers."
        image={IMG.diamondRow}
      />

      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {HELP.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div className="glass rounded-2xl p-7 h-full hover:border-bb-gold/25 transition-colors duration-500">
                <Icon className="h-6 w-6 text-bb-gold" strokeWidth={1} />
                <h3 className="mt-5 font-serif text-2xl">{t}</h3>
                <p className="mt-2 text-sm text-bb-silver/65 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <Reveal><Eyebrow>The Buyer Pathway</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl max-w-3xl leading-tight">From enquiry to an owned, verified diamond.</h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            "Request access to verified inventory",
            "Specify size, colour, clarity and quantity",
            "Request a memo or purchase introduction",
            "Identify the lawful seller or manufacturer",
            "Transfer the certificate after purchase",
          ].map((step, i) => (
            <Reveal key={step} delay={i * 0.08}>
              <div className="glass rounded-2xl p-6 h-full">
                <div className="font-serif text-bb-gold text-3xl">{`0${i + 1}`}</div>
                <p className="mt-3 text-sm text-bb-silver/75 leading-relaxed">{step}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <Reveal>
          <div className="glass rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-8">
            <ShieldCheck className="h-12 w-12 text-bb-gold shrink-0" strokeWidth={0.9} />
            <div className="flex-1">
              <h2 className="font-serif text-3xl md:text-4xl">Access is controlled and participants are verified.</h2>
              <p className="mt-3 text-sm text-bb-silver/65 leading-relaxed max-w-3xl">
                International buyers may request information or make an enquiry. Botswana Brilliance controls access and verifies
                participants before releasing sensitive information. The platform is built for verified inventory, not as a public
                commodity marketplace.
              </p>
            </div>
            <Link to="/contact" className="btn-gold rounded-full px-7 py-3.5 text-sm whitespace-nowrap">Make an enquiry</Link>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
